import { Card, CardContent, Typography, Box } from '@mui/material'
import { alpha, keyframes, useTheme } from '@mui/material/styles'
import { tokens } from '../../../theme'

const countUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

/**
 * TrendCard — Value with inline SVG sparkline showing direction
 * Use for metrics with trend data (monthly savings, performance over time)
 */
const TrendCard = ({
  title,
  value,
  subtitle,
  trendData = [],
  trendLabel,
  icon,
  color = '#A0CE4E',
  onClick
}) => {
  const theme = useTheme()
  const palette = theme.palette

  // Build sparkline path from trend data
  const sparklineWidth = 80
  const sparklineHeight = 28
  const buildSparkline = () => {
    if (!trendData || trendData.length < 2) return null

    const min = Math.min(...trendData)
    const max = Math.max(...trendData)
    const range = max - min || 1

    const points = trendData.map((val, i) => {
      const x = (i / (trendData.length - 1)) * sparklineWidth
      const y = sparklineHeight - ((val - min) / range) * (sparklineHeight - 4) - 2
      return `${x},${y}`
    })

    // Build gradient fill path
    const firstPoint = points[0].split(',')
    const lastPoint = points[points.length - 1].split(',')
    const fillPath = `M ${firstPoint[0]},${sparklineHeight} L ${points.join(' L ')} L ${lastPoint[0]},${sparklineHeight} Z`

    return {
      linePath: `M ${points.join(' L ')}`,
      fillPath,
      lastY: parseFloat(points[points.length - 1].split(',')[1])
    }
  }

  const sparkline = buildSparkline()

  // Determine trend direction
  const trendDirection = trendData.length >= 2
    ? trendData[trendData.length - 1] > trendData[0] ? 'up' : 'down'
    : null

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: `all ${tokens.transitions.normal}`,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          borderColor: alpha(color, 0.3),
          boxShadow: `0 4px 16px rgba(0,0,0,0.15), 0 0 20px ${alpha(color, 0.1)}`
        } : {
          borderColor: alpha(palette.text.primary, 0.1)
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.4)} 100%)`,
          opacity: 0.8
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, pb: { xs: '16px !important', sm: '20px !important' } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.7rem',
                letterSpacing: '0.1em',
                lineHeight: 1.5,
                display: 'block',
                mb: 0.75
              }}
            >
              {title}
            </Typography>

            {/* Value */}
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 700,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                color: 'text.primary',
                lineHeight: 1,
                letterSpacing: '-0.02em',
                animation: `${countUp} 0.5s ease-out`
              }}
            >
              {value}
            </Typography>

            {/* Trend label */}
            {trendLabel && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                {trendDirection && (
                  <Typography
                    sx={{
                      color: trendDirection === 'up' ? palette.success.main : palette.error.main,
                      fontWeight: 700,
                      fontSize: '0.7rem'
                    }}
                  >
                    {trendDirection === 'up' ? '↑' : '↓'}
                  </Typography>
                )}
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 500
                  }}
                >
                  {trendLabel}
                </Typography>
              </Box>
            )}

            {/* Subtitle */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: trendLabel ? 0.5 : 1,
                fontSize: '0.75rem',
                lineHeight: 1.4
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* Sparkline or Icon */}
          <Box
            sx={{
              ml: { xs: 1, sm: 1.5 },
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5
            }}
          >
            {sparkline ? (
              <svg width={sparklineWidth} height={sparklineHeight} style={{ display: 'block' }}>
                {/* Gradient fill under line */}
                <defs>
                  <linearGradient id={`trend-fill-${title?.replace(/\s/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <path
                  d={sparkline.fillPath}
                  fill={`url(#trend-fill-${title?.replace(/\s/g, '')})`}
                />
                {/* Line */}
                <path
                  d={sparkline.linePath}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* End dot */}
                <circle
                  cx={sparklineWidth}
                  cy={sparkline.lastY}
                  r={3}
                  fill={color}
                />
              </svg>
            ) : icon ? (
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
                  borderRadius: tokens.radius.sm,
                  p: { xs: 0.75, sm: 1, md: 1.25 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 16px ${alpha(color, 0.3)}`,
                  '& .MuiSvgIcon-root': {
                    color: palette.primary.contrastText,
                    fontSize: { xs: 18, sm: 20, md: 24 }
                  }
                }}
              >
                {icon}
              </Box>
            ) : null}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default TrendCard
